/**
 * ProductionAnalysisTab — phân tích hiệu suất sản xuất + extracted value
 * (spec §4 — `Excel_Production_Report_Web_UI_Conversion_Spec.md`).
 *
 * Tables: áp CSS skill `/table-style` (header gradient vàng/teal + cell ellipsis
 * + body striping). Section header text + chip (no colored bar). Variance color
 * text only.
 *
 * Sections:
 *   1. Header context (BOM / LIFFE / G2 / mode badge) — info card.
 *   2. R13-ST8 quality deduction card — small table.
 *   3. INPUT Budget vs Actual grid (grouped header Identity/Budget/...).
 *   4. OUTPUT Budget vs Actual grid (same pattern).
 *   5. LOSS KPI strip + EXTRACTED VALUE / P&L summary.
 */
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import {
  EXTRACTED_VALUE_SUMMARY,
  INPUT_BUDGET_ACTUAL_ROWS,
  LOSS_SUMMARY,
  OUTPUT_BUDGET_ACTUAL_ROWS,
  PRODUCTION_ANALYSIS_HEADER,
  QUALITY_DEDUCTION_ROWS,
  type BudgetActualRow,
} from '../productionReportMock'
import {
  fmtMt,
  fmtPercentScaled,
  fmtPlain,
  fmtUsd,
  fmtUsdPrecise,
  fmtVarPct,
} from './formatters'
import {
  COLORS,
  kpiAccent,
  sectionCardSx,
  sectionHeaderSx,
  sectionTitleSx,
  varianceColor,
  type KpiTone,
} from './styleTokens'

/* ---------- header context strip ---------------------------------------- */

function HeaderContext() {
  const h = PRODUCTION_ANALYSIS_HEADER
  return (
    <Box sx={{ ...sectionCardSx, p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
        <Stack spacing={0.25}>
          <Typography sx={{ fontSize: '0.72rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
            Production analysis
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: COLORS.text }}>PO {h.poNumber}</Typography>
        </Stack>
        <Box sx={{ width: 1, height: 36, bgcolor: COLORS.border }} />
        <Meta label="BOM" value={h.bomCode} />
        <Meta label="LIFFE" value={fmtUsd(h.liffePrice)} />
        <Meta label="G2 Diffs" value={fmtUsd(h.g2Diffs)} />
        <Meta label="Input" value={h.inputCategorySummary} />
        <Meta label="Output" value={h.outputCategorySummary} />
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          label={h.mode === 'qui chuan' ? 'Standardized' : 'Non-standardized'}
          sx={{
            height: 24,
            fontSize: '0.72rem',
            fontWeight: 600,
            bgcolor: h.mode === 'qui chuan' ? COLORS.successSoft : COLORS.bgHeader,
            color: h.mode === 'qui chuan' ? COLORS.success : COLORS.textMuted,
            border: `1px solid ${h.mode === 'qui chuan' ? COLORS.success : COLORS.border}`,
          }}
        />
      </Stack>
    </Box>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <Stack spacing={0.25}>
      <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: COLORS.text }}>{value}</Typography>
    </Stack>
  )
}

/* ---------- R13-ST8 deduction card ------------------------------------- */

function QualityDeductionCard() {
  const rows = QUALITY_DEDUCTION_ROWS
  if (rows.length === 0) return null
  return (
    <Stack spacing={0.75}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={sectionTitleSx}>Quality deduction</Typography>
        <Chip
          size="small"
          label="Items above FM standard"
          sx={{
            height: 20, fontSize: '0.7rem', fontWeight: 600,
            bgcolor: COLORS.warnSoft, color: COLORS.warn, border: 'none',
          }}
        />
      </Stack>
      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className="app-table-header-cell">Item</TableCell>
                <TableCell align="right" className="app-table-header-cell">FM output</TableCell>
                <TableCell align="right" className="app-table-header-cell">FM standard</TableCell>
                <TableCell align="right" className="app-table-header-cell">Weight (Mt)</TableCell>
                <TableCell align="right" className="app-table-header-cell">% deducted</TableCell>
                <TableCell align="right" className="app-table-header-cell">Weight deducted (Mt)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const overStd = r.fmOutput > r.fmStandard
                return (
                  <TableRow key={r.item}>
                    <TableCell><CellText value={r.item} /></TableCell>
                    <TableCell align="right" sx={{ color: overStd ? COLORS.danger : COLORS.text, fontWeight: overStd ? 700 : 400 }}>
                      {fmtPlain(r.fmOutput, 2)}
                    </TableCell>
                    <TableCell align="right">{fmtPlain(r.fmStandard, 2)}</TableCell>
                    <TableCell align="right">{fmtPlain(r.weight, 2)}</TableCell>
                    <TableCell align="right">{fmtPercentScaled(r.pctDeduct, 1)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.danger }}>
                      {fmtPlain(r.weightDeduct, 3)}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Stack>
  )
}

function CellText({ value }: { value: string | number }) {
  const s = String(value)
  return (
    <Box component="span" className="app-table-cell-text" sx={{ maxWidth: 'none' }} title={s}>
      {s}
    </Box>
  )
}

/* ---------- Budget vs Actual grid -------------------------------------- */

interface BudgetGridProps {
  title: 'INPUT' | 'OUTPUT'
  rows: BudgetActualRow[]
}

interface BAColumn {
  key: keyof BudgetActualRow
  label: string
  group: 'identity' | 'budget' | 'actual' | 'value' | 'quality'
  width: number
  kind: 'text' | 'pct' | 'var' | 'usd' | 'usdPerMt' | 'mt' | 'plain'
  sticky?: boolean
}

const BA_COLUMNS: BAColumn[] = [
  { key: 'code', label: 'Code', group: 'identity', width: 110, kind: 'text', sticky: true },
  { key: 'category', label: 'Category', group: 'identity', width: 120, kind: 'text', sticky: true },
  { key: 'premium', label: 'Premium', group: 'identity', width: 90, kind: 'usd' },
  { key: 'bomPct', label: 'BOM %', group: 'budget', width: 80, kind: 'pct' },
  { key: 'actualMt', label: 'Actual Mt', group: 'actual', width: 100, kind: 'mt' },
  { key: 'actualPct', label: 'Actual %', group: 'actual', width: 80, kind: 'pct' },
  { key: 'varPct', label: 'Var %', group: 'actual', width: 90, kind: 'var' },
  { key: 'valuePerMt', label: 'Value / Mt', group: 'value', width: 110, kind: 'usdPerMt' },
  { key: 'totalValue', label: 'Total value', group: 'value', width: 130, kind: 'usd' },
  { key: 'mc', label: 'MC', group: 'quality', width: 70, kind: 'plain' },
  { key: 'blacks', label: 'Blacks', group: 'quality', width: 75, kind: 'plain' },
  { key: 'brokens', label: 'Brokens', group: 'quality', width: 80, kind: 'plain' },
  { key: 'blackBroken', label: 'BB', group: 'quality', width: 70, kind: 'plain' },
  { key: 'fm', label: 'FM', group: 'quality', width: 70, kind: 'plain' },
  { key: 's18', label: 'S18', group: 'quality', width: 70, kind: 'plain' },
  { key: 's16', label: 'S16', group: 'quality', width: 70, kind: 'plain' },
  { key: 's13', label: 'S13', group: 'quality', width: 70, kind: 'plain' },
]

function fmtCell(val: unknown, kind: BAColumn['kind']): string {
  if (val == null || val === '') return ''
  const n = Number(val)
  switch (kind) {
    case 'pct': return fmtPercentScaled(n)
    case 'var': return fmtVarPct(n)
    case 'usd': return fmtUsd(n)
    case 'usdPerMt': return fmtUsdPrecise(n)
    case 'mt': return fmtMt(n)
    case 'plain': return fmtPlain(n, 2)
    default: return String(val)
  }
}

function groupLabel(group: BAColumn['group']): string {
  switch (group) {
    case 'identity': return ''
    case 'budget': return 'Budget'
    case 'actual': return 'Actual'
    case 'value': return 'Value'
    case 'quality': return 'Quality'
  }
}

function aggregateTotals(rows: BudgetActualRow[]): Partial<BudgetActualRow> {
  if (rows.length === 0) return {}
  const mtTotal = rows.reduce((s, r) => s + (r.actualMt || 0), 0)
  const bomTotal = rows.reduce((s, r) => s + (r.bomPct || 0), 0)
  const actualPctTotal = rows.reduce((s, r) => s + (r.actualPct || 0), 0)
  const varTotal = rows.reduce((s, r) => s + (r.varPct || 0), 0)
  const valueTotal = rows.reduce((s, r) => s + (r.totalValue || 0), 0)
  const wavg = (key: keyof BudgetActualRow) =>
    mtTotal > 0
      ? rows.reduce((s, r) => s + Number(r[key] ?? 0) * (r.actualMt || 0), 0) / mtTotal
      : 0
  return {
    code: 'TOTAL',
    category: '',
    bomPct: bomTotal,
    actualMt: mtTotal,
    actualPct: actualPctTotal,
    varPct: varTotal,
    totalValue: valueTotal,
    mc: wavg('mc'),
    blacks: wavg('blacks'),
    brokens: wavg('brokens'),
    blackBroken: wavg('blackBroken'),
    fm: wavg('fm'),
    s18: wavg('s18'),
    s16: wavg('s16'),
    s13: wavg('s13'),
  }
}

const groupHeaderCellSx: SxProps<Theme> = {
  fontSize: '0.65rem',
  fontWeight: 700,
  py: 0.4,
  px: 0.5,
  color: COLORS.textMuted,
  bgcolor: COLORS.bgSubtle,
  border: 'none',
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  position: 'sticky',
  top: 0,
  zIndex: 4,
}

function BudgetGrid({ title, rows }: BudgetGridProps) {
  const stickyOffsets: Record<string, number> = {}
  let acc = 0
  for (const col of BA_COLUMNS) {
    if (col.sticky) {
      stickyOffsets[col.key] = acc
      acc += col.width
    }
  }

  const groupSpans: { group: BAColumn['group']; span: number }[] = []
  for (const col of BA_COLUMNS) {
    const last = groupSpans[groupSpans.length - 1]
    if (last && last.group === col.group) last.span += 1
    else groupSpans.push({ group: col.group, span: 1 })
  }

  const totals = aggregateTotals(rows)

  return (
    <Stack spacing={0.75}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={sectionTitleSx}>{title} — Budget vs Actual</Typography>
        <Chip
          size="small"
          label={`${rows.length} items`}
          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: COLORS.bgHeader, color: COLORS.textMuted, border: 'none' }}
        />
      </Stack>
      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap">
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead>
              {/* Group header row — subtle bg (KHÔNG dùng app-table-header-cell). */}
              <TableRow>
                {groupSpans.map((g, i) => (
                  <TableCell
                    key={`g-${i}`}
                    colSpan={g.span}
                    align="center"
                    sx={groupHeaderCellSx}
                  >
                    {groupLabel(g.group)}
                  </TableCell>
                ))}
              </TableRow>
              {/* Column header row — gradient via app-table-header-cell. */}
              <TableRow>
                {BA_COLUMNS.map((col) => (
                  <TableCell
                    key={col.key as string}
                    align={col.kind === 'text' ? 'left' : 'right'}
                    className="app-table-header-cell"
                    style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                    sx={{
                      whiteSpace: 'nowrap',
                      top: 26,   // group row chiếm row đầu tiên
                      ...(col.sticky && {
                        position: 'sticky',
                        left: stickyOffsets[col.key as string],
                        zIndex: 6,
                      }),
                    }}
                    title={col.label}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx}>
                  {BA_COLUMNS.map((col) => {
                    const v = row[col.key]
                    const text = fmtCell(v, col.kind)
                    const isVar = col.kind === 'var'
                    const varN = isVar ? Number(v) || 0 : 0
                    return (
                      <TableCell
                        key={col.key as string}
                        align={col.kind === 'text' ? 'left' : 'right'}
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: isVar ? varianceColor(varN) : undefined,
                          fontWeight: isVar ? 600 : undefined,
                          ...(col.sticky && {
                            position: 'sticky',
                            left: stickyOffsets[col.key as string],
                            zIndex: 2,
                            bgcolor: 'background.paper',
                            borderRight: `1px solid ${COLORS.border}`,
                          }),
                        }}
                        title={text}
                      >
                        {text}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
              {rows.length > 0 ? (
                <TableRow>
                  {BA_COLUMNS.map((col) => {
                    const v = totals[col.key]
                    const text = col.kind === 'text' ? (col.key === 'code' ? 'TOTAL' : '') : fmtCell(v, col.kind)
                    const isVar = col.kind === 'var'
                    const varN = isVar ? Number(v) || 0 : 0
                    return (
                      <TableCell
                        key={col.key as string}
                        align={col.kind === 'text' ? 'left' : 'right'}
                        style={{ width: col.width, minWidth: col.width, maxWidth: col.width }}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          bgcolor: COLORS.bgSubtle,
                          borderTop: `1px solid ${COLORS.borderStrong}`,
                          borderBottom: 'none',
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 3,
                          color: isVar ? varianceColor(varN) : undefined,
                          ...(col.sticky && {
                            left: stickyOffsets[col.key as string],
                            zIndex: 4,
                            borderRight: `1px solid ${COLORS.border}`,
                          }),
                        }}
                      >
                        {text}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Stack>
  )
}

/* ---------- Loss strip --------------------------------------------------- */

function LossStrip() {
  const l = LOSS_SUMMARY
  const unexpHigh = l.unexpLossPct > 0.03
  return (
    <Box sx={sectionCardSx}>
      <Box sx={sectionHeaderSx}>
        <Typography sx={sectionTitleSx}>Loss analysis</Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <MetricCard label="Total loss" value={`${fmtPlain(l.totalLoss, 3)} Mt`} sub={fmtPercentScaled(l.totalLossPct)} tone="danger" />
          <MetricCard label="MC in" value={fmtPlain(l.mcIn)} />
          <MetricCard label="MC out" value={fmtPlain(l.mcOut)} />
          <MetricCard label="MC loss" value={`${fmtPlain(l.mcLoss, 2)} Mt`} sub={fmtPercentScaled(l.mcLossPct)} />
          <MetricCard
            label="Unexplained loss"
            value={`${fmtPlain(l.unexpLoss, 3)} Mt`}
            sub={fmtPercentScaled(l.unexpLossPct)}
            tone={unexpHigh ? 'danger' : 'warn'}
          />
          <MetricCard label="BOM loss" value={fmtPercentScaled(l.bomLoss)} />
          <MetricCard label="Variance vs BOM" value={fmtVarPct(l.variance)} tone={l.variance >= 0 ? 'success' : 'danger'} />
        </Stack>
      </Box>
    </Box>
  )
}

/* ---------- Extracted value summary ------------------------------------- */

function ExtractedValueSection() {
  const e = EXTRACTED_VALUE_SUMMARY
  const pnlTone: KpiTone = e.totalPnl >= 0 ? 'success' : 'danger'
  return (
    <Box sx={sectionCardSx}>
      <Box sx={sectionHeaderSx}>
        <Typography sx={sectionTitleSx}>Extracted value & P&amp;L</Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <MetricCard label="Value in" value={fmtUsd(e.valueIn)} />
          <MetricCard label="Value out" value={fmtUsd(e.valueOut)} />
          <MetricCard label="Value extracted" value={fmtUsd(e.valueExtracted)} tone="success" />
          <MetricCard label="Extracted / Mt" value={fmtUsdPrecise(e.valueExtractedPerMt)} />
          <MetricCard label="Handling cost" value={fmtUsdPrecise(e.handling)} />
          <MetricCard label="Extracted / Mt (net)" value={fmtUsdPrecise(e.valueExtractedPerMtAfterCost)} />
          <MetricCard label="Actual extracted" value={fmtUsd(e.actualExtracted)} />
          <MetricCard label="BOM extracted" value={fmtUsd(e.bomExtracted)} />
          <MetricCard label="P&L / Mt" value={fmtUsdPrecise(e.pnlPerMt)} tone={pnlTone} />
          <MetricCard label="Total P&L" value={fmtUsd(e.totalPnl)} tone={pnlTone} />
        </Stack>
      </Box>
    </Box>
  )
}

/* ---------- shared metric card ----------------------------------------- */

interface MetricCardProps {
  label: string
  value: string
  sub?: string
  tone?: KpiTone
}

function MetricCard({ label, value, sub, tone = 'neutral' }: MetricCardProps) {
  const stripe = kpiAccent(tone)
  const valueColor = tone === 'danger' ? COLORS.danger : tone === 'success' ? COLORS.success : tone === 'warn' ? COLORS.warn : COLORS.text
  return (
    <Box
      sx={{
        flex: '1 1 0',
        minWidth: 150,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 1.5,
        bgcolor: COLORS.bgPage,
        px: 1.75,
        py: 1.25,
        borderLeft: `3px solid ${stripe}`,
      }}
    >
      <Typography sx={{ fontSize: '0.7rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: valueColor, lineHeight: 1.2, mt: 0.4 }}>
        {value}
      </Typography>
      {sub ? <Typography sx={{ fontSize: '0.72rem', color: COLORS.textSubtle, mt: 0.25 }}>{sub}</Typography> : null}
    </Box>
  )
}

/* ---------- main tab ----------------------------------------------------- */

export default function ProductionAnalysisTab() {
  return (
    <Stack spacing={2}>
      <HeaderContext />
      <QualityDeductionCard />
      <BudgetGrid title="INPUT" rows={INPUT_BUDGET_ACTUAL_ROWS} />
      <BudgetGrid title="OUTPUT" rows={OUTPUT_BUDGET_ACTUAL_ROWS} />
      <LossStrip />
      <ExtractedValueSection />
    </Stack>
  )
}
